/**
 * useSubunitDragInteraction — Drag, select, and keyboard interactions for subunits
 *
 * Simplified version of useObstacleDragInteraction:
 * - Click to select, drag to move, Delete to remove, Ctrl+D to duplicate, Escape to deselect
 * - Click-to-place during placement mode
 * - No rotation (axis-aligned per spec)
 * - Containment clamping: position stays within parent unit bounds
 */

import { useEffect, useRef, useCallback } from 'react'
import * as THREE from 'three'
import { useQueryClient } from '@tanstack/react-query'
import { useSubunitStore } from '@/stores/subunitStore'
import { useSubunitMutations, subunitKeys } from '@/hooks/useSubunits'
import { screenToWorldPosition } from '@/utils/coordinates'
import { METERS_TO_FEET } from '@/constants/units'
import type { Subunit, CreateSubunitInput } from '@/types/subunit'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'

interface UseSubunitDragInteractionParams {
  map: mapboxgl.Map | null
  sceneObjects: {
    scene: THREE.Scene | null
    camera: THREE.Camera | null
    groundPlane: THREE.Mesh | null
  } | null
  enabled: boolean
  subunits: Subunit[]
  locationId: string
  buildings: Building3DConfigExtended[]
  editingBuildingId: string | null
  editingUnitId: string | null
  mapCenter: { lat: number; lng: number } | null
  onSubunitUpdate?: () => void
}

const DRAG_THRESHOLD_PIXELS = 5

/** Clamp subunit offset to stay within parent unit bounds.
 *  X axis: edge-origin (0 = left edge of parent unit)
 *  Y axis: center-origin (0 = center of parent unit depth) */
function clampToParent(
  x: number, y: number,
  subW: number, subD: number,
  parentW: number, parentD: number,
): [number, number] {
  const halfSubW = subW / 2
  const halfSubD = subD / 2

  // X: edge-origin — range [halfSub, parentW - halfSub]
  const minX = halfSubW
  const maxX = parentW - halfSubW
  // Y: center-origin — range [-halfParent + halfSub, halfParent - halfSub]
  const halfParentD = parentD / 2
  const minY = -halfParentD + halfSubD
  const maxY = halfParentD - halfSubD

  return [
    minX > maxX ? parentW / 2 : Math.max(minX, Math.min(maxX, x)),
    minY > maxY ? 0 : Math.max(minY, Math.min(maxY, y)),
  ]
}

export function useSubunitDragInteraction({
  map,
  sceneObjects,
  enabled,
  subunits,
  locationId,
  buildings,
  editingBuildingId,
  editingUnitId,
  mapCenter,
  onSubunitUpdate,
}: UseSubunitDragInteractionParams) {
  const {
    selectedSubunitId, selectSubunit,
    isPlacingMode, placingDefaults, cancelPlacing,
    isCreatingMode, creatingDefaults, cancelCreating,
    setDragging, setPreviewPosition,
  } = useSubunitStore()
  const { create, remove, duplicate } = useSubunitMutations()
  const queryClient = useQueryClient()

  const isDragging = useRef(false)
  const dragStartScreen = useRef<[number, number]>([0, 0])
  const dragStartWorld = useRef<[number, number]>([0, 0])
  const dragStartSubunitPos = useRef<[number, number]>([0, 0])
  const dragSubunitId = useRef<string | null>(null)
  const dragBuildingRotDeg = useRef<number>(0)

  // Get scene position of a subunit (in scene feet)
  const getSubunitScenePosition = useCallback(
    (subunit: Subunit): { x: number; z: number } | null => {
      const building = buildings.find(b =>
        b.generatedUnits?.some(u => u.id === subunit.parentUnitId)
      )
      if (!building?.generatedUnits) return null

      const parentUnit = building.generatedUnits.find(u => u.id === subunit.parentUnitId)
      if (!parentUnit) return null

      const centerX = building.width / 2
      const tpl = building.templateType as string | undefined
      const centerZ = (tpl === 'double_row' || tpl === 'nested_t_hangar') ? building.depth / 2 : 0

      const adjOffsetX = parentUnit.offset_x - centerX
      const adjOffsetY = parentUnit.offset_y - centerZ

      const localX = adjOffsetX + subunit.offsetX
      const localZ = -(adjOffsetY + subunit.offsetY)

      const rotDeg = building.rotation ?? 0
      const rad = (rotDeg * Math.PI) / 180
      const cos = Math.cos(rad)
      const sin = Math.sin(rad)
      const rotatedX = localX * cos + localZ * sin
      const rotatedZ = -localX * sin + localZ * cos

      return {
        x: building.position[0] + rotatedX,
        z: building.position[2] + rotatedZ,
      }
    },
    [buildings]
  )

  // Hit test subunits (AABB-based)
  const hitTestSubunits = useCallback(
    (screenX: number, screenY: number): string | null => {
      if (!map || !sceneObjects?.scene || !mapCenter) return null

      const canvas = map.getCanvas()
      const rect = canvas.getBoundingClientRect()
      const ndc = new THREE.Vector2(
        ((screenX - rect.left) / rect.width) * 2 - 1,
        -((screenY - rect.top) / rect.height) * 2 + 1
      )

      const worldPos = screenToWorldPosition(ndc, map, [mapCenter.lng, mapCenter.lat])
      if (!worldPos) return null

      const clickX = worldPos.x * METERS_TO_FEET
      const clickZ = worldPos.z * METERS_TO_FEET

      // Filter to editing building's subunits
      const editingSubunits = editingUnitId
        ? subunits.filter(s => s.parentUnitId === editingUnitId)
        : subunits

      let bestId: string | null = null
      let bestDist = Infinity

      for (const subunit of editingSubunits) {
        const scenePos = getSubunitScenePosition(subunit)
        if (!scenePos) continue

        const dx = clickX - scenePos.x
        const dz = clickZ - scenePos.z

        // Find building rotation for this subunit
        const buildingRot = buildings.find(b =>
          b.generatedUnits?.some(u => u.id === subunit.parentUnitId)
        )?.rotation ?? 0

        // Rotate click delta into building-local frame (subunits are axis-aligned within building)
        const rotRad = -buildingRot * Math.PI / 180
        const cos = Math.cos(rotRad)
        const sin = Math.sin(rotRad)
        const localDx = dx * cos - dz * sin
        const localDz = dx * sin + dz * cos
        const halfW = subunit.width / 2
        const halfD = subunit.depth / 2

        if (Math.abs(localDx) <= halfW && Math.abs(localDz) <= halfD) {
          const dist = Math.sqrt(dx * dx + dz * dz)
          if (dist < bestDist) {
            bestDist = dist
            bestId = subunit.id
          }
        }
      }

      return bestId
    },
    [map, sceneObjects, mapCenter, editingUnitId, buildings, subunits, getSubunitScenePosition]
  )

  const getWorldPosition = useCallback(
    (screenX: number, screenY: number): { sceneX: number; sceneZ: number } | null => {
      if (!map || !sceneObjects?.scene || !mapCenter) return null

      const canvas = map.getCanvas()
      const rect = canvas.getBoundingClientRect()
      const ndc = new THREE.Vector2(
        ((screenX - rect.left) / rect.width) * 2 - 1,
        -((screenY - rect.top) / rect.height) * 2 + 1
      )

      const worldPos = screenToWorldPosition(ndc, map, [mapCenter.lng, mapCenter.lat])
      if (!worldPos) return null

      return { sceneX: worldPos.x * METERS_TO_FEET, sceneZ: worldPos.z * METERS_TO_FEET }
    },
    [map, sceneObjects, mapCenter]
  )

  // Unified dimension source: placingDefaults (panel mode) OR creatingDefaults (view-mode)
  const activePlacingMode = isPlacingMode || isCreatingMode
  const activeDefaults = placingDefaults ?? creatingDefaults

  // Auto-detect which building a click falls within (for view-mode creating)
  const detectBuildingAtPosition = useCallback(
    (sceneXFeet: number, sceneZFeet: number): Building3DConfigExtended | null => {
      for (const building of buildings) {
        const bx = building.position[0]
        const bz = building.position[2]
        const rotDeg = building.rotation ?? 0

        // Transform click into building-local space
        let relX = sceneXFeet - bx
        let relZ = sceneZFeet - bz
        if (rotDeg !== 0) {
          const rad = (rotDeg * Math.PI) / 180
          const cos = Math.cos(rad)
          const sin = Math.sin(rad)
          const rx = relX * cos - relZ * sin
          const rz = relX * sin + relZ * cos
          relX = rx
          relZ = rz
        }

        const tpl = building.templateType as string | undefined
        const halfW = building.width / 2
        const centerZ = (tpl === 'double_row' || tpl === 'nested_t_hangar') ? building.depth / 2 : 0
        const minZ = -centerZ
        const maxZ = building.depth - centerZ

        if (relX >= -halfW && relX <= halfW && -relZ >= minZ && -relZ <= maxZ) {
          return building
        }
      }
      return null
    },
    [buildings]
  )

  // Click-to-place from category defaults
  const handlePlaceSubunit = useCallback(
    (sceneXFeet: number, sceneZFeet: number) => {
      if (!activeDefaults || !locationId) return

      // For creating mode, auto-detect the building at click position if no target set
      // For placing mode (panel), use editingBuildingId/editingUnitId
      let targetBuildingId: string | null
      let targetUnitId: string | null

      if (isCreatingMode) {
        const storeTarget = useSubunitStore.getState().creatingForBuildingId
        if (storeTarget) {
          targetBuildingId = storeTarget
          targetUnitId = editingUnitId
        } else {
          // Auto-detect building from click position
          const detected = detectBuildingAtPosition(sceneXFeet, sceneZFeet)
          if (!detected) return
          targetBuildingId = detected.id
          targetUnitId = null // will pick first unit below
        }
      } else {
        targetBuildingId = editingBuildingId
        targetUnitId = editingUnitId
      }

      if (!targetBuildingId) return

      const building = buildings.find(b => b.id === targetBuildingId)
      if (!building?.generatedUnits?.length) return

      // For creating mode, find the first unit of the building (community hangars have 1 unit)
      const parentUnit = targetUnitId
        ? building.generatedUnits.find(u => u.id === targetUnitId)
        : building.generatedUnits[0]
      if (!parentUnit) return

      // Convert scene-absolute feet → parent-unit-local feet
      const buildingPosX = building.position[0]
      const buildingPosZ = building.position[2]
      const buildingRotDeg = building.rotation ?? 0

      let relX = sceneXFeet - buildingPosX
      let relZ = sceneZFeet - buildingPosZ

      if (buildingRotDeg !== 0) {
        const rad = (buildingRotDeg * Math.PI) / 180
        const cos = Math.cos(rad)
        const sin = Math.sin(rad)
        const rx = relX * cos - relZ * sin
        const rz = relX * sin + relZ * cos
        relX = rx
        relZ = rz
      }

      const centerX = building.width / 2
      const tpl = building.templateType as string | undefined
      const centerZ = (tpl === 'double_row' || tpl === 'nested_t_hangar') ? building.depth / 2 : 0
      const adjOffsetX = parentUnit.offset_x - centerX
      const adjOffsetY = parentUnit.offset_y - centerZ

      let offsetX = relX - adjOffsetX
      let offsetY = -relZ - adjOffsetY

      // Containment clamping for placement
      if (useSubunitStore.getState().constrainToParent) {
        ;[offsetX, offsetY] = clampToParent(
          offsetX, offsetY,
          activeDefaults.width, activeDefaults.depth,
          parentUnit.interior_width, parentUnit.interior_depth,
        )
      }

      const input: CreateSubunitInput = {
        parentUnitId: parentUnit.id,
        areaId: building.id, // area_id = building id
        locationId,
        name: activeDefaults.name,
        unitCode: `SU-${Date.now().toString(36).toUpperCase()}`,
        category: activeDefaults.category,
        width: activeDefaults.width,
        depth: activeDefaults.depth,
        height: activeDefaults.height,
        offsetX,
        offsetY,
        elevationFt: activeDefaults.elevationFt,
        clearanceBufferFt: activeDefaults.clearanceBufferFt,
        deductFromParent: activeDefaults.deductFromParent,
      }

      create.mutate(input, {
        onSuccess: (newSubunit) => {
          if (isCreatingMode) {
            cancelCreating()
          } else {
            cancelPlacing()
          }
          if (newSubunit?.id) {
            selectSubunit(newSubunit.id)
          }
        },
      })

      // Optimistically close the mode
      if (isCreatingMode) {
        cancelCreating()
      } else {
        cancelPlacing()
      }
    },
    [activeDefaults, isCreatingMode, locationId, editingBuildingId, editingUnitId, buildings, create, cancelPlacing, cancelCreating, selectSubunit, detectBuildingAtPosition]
  )

  // Mouse handlers
  useEffect(() => {
    if (!map || !enabled) return

    const canvas = map.getCanvas()

    const onMouseDown = (e: mapboxgl.MapMouseEvent) => {
      const { clientX, clientY } = e.originalEvent
      dragStartScreen.current = [clientX, clientY]
      isDragging.current = false

      if (activePlacingMode) return

      // Only allow drag if the subunit is already selected (via gear icon in labels overlay)
      // Do NOT select on click — selection is gear-gated
      if (selectedSubunitId) {
        const hitId = hitTestSubunits(clientX, clientY)
        if (hitId === selectedSubunitId) {
          dragSubunitId.current = hitId
          const world = getWorldPosition(clientX, clientY)
          if (world) dragStartWorld.current = [world.sceneX, world.sceneZ]
          const sub = subunits.find(s => s.id === hitId)
          if (sub) dragStartSubunitPos.current = [sub.offsetX, sub.offsetY]
          const buildingRot = buildings.find(b =>
            b.generatedUnits?.some(u => u.id === sub?.parentUnitId)
          )?.rotation ?? 0
          dragBuildingRotDeg.current = buildingRot
          e.preventDefault()
        } else {
          dragSubunitId.current = null
        }
      } else {
        dragSubunitId.current = null
      }
    }

    const onMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const { clientX, clientY } = e.originalEvent

      if (activePlacingMode) {
        canvas.style.cursor = 'crosshair'
        const world = getWorldPosition(clientX, clientY)
        if (world) setPreviewPosition({ x: world.sceneX, z: world.sceneZ })
        return
      }

      // Hover detection — only show move cursor over the selected subunit (gear-gated)
      if (!dragSubunitId.current || !isDragging.current) {
        if (selectedSubunitId) {
          const hoverId = hitTestSubunits(clientX, clientY)
          if (hoverId === selectedSubunitId) {
            canvas.style.cursor = 'move'
            useSubunitStore.getState().setHovered(hoverId)
            return
          }
        }
        useSubunitStore.getState().setHovered(null)
        if (!dragSubunitId.current) return
      }

      // Drag
      const dx = clientX - dragStartScreen.current[0]
      const dy = clientY - dragStartScreen.current[1]
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist < DRAG_THRESHOLD_PIXELS) return

      if (!isDragging.current) {
        isDragging.current = true
        setDragging(true)
        map.dragPan.disable()
      }

      canvas.style.cursor = 'move'

      const world = getWorldPosition(clientX, clientY)
      if (!world) return

      let totalDeltaX = world.sceneX - dragStartWorld.current[0]
      let totalDeltaZ = world.sceneZ - dragStartWorld.current[1]

      // Inverse-rotate into building-local space
      if (dragBuildingRotDeg.current !== 0) {
        const rad = (dragBuildingRotDeg.current * Math.PI) / 180
        const cos = Math.cos(rad)
        const sin = Math.sin(rad)
        const rx = totalDeltaX * cos - totalDeltaZ * sin
        const rz = totalDeltaX * sin + totalDeltaZ * cos
        totalDeltaX = rx
        totalDeltaZ = rz
      }

      let newX = dragStartSubunitPos.current[0] + totalDeltaX
      let newY = dragStartSubunitPos.current[1] - totalDeltaZ

      // Containment clamping
      if (useSubunitStore.getState().constrainToParent) {
        const sub = subunits.find(s => s.id === dragSubunitId.current)
        if (sub) {
          const building = buildings.find(b =>
            b.generatedUnits?.some(u => u.id === sub.parentUnitId)
          )
          const parentUnit = building?.generatedUnits?.find(u => u.id === sub.parentUnitId)
          if (parentUnit) {
            ;[newX, newY] = clampToParent(
              newX, newY,
              sub.width, sub.depth,
              parentUnit.interior_width, parentUnit.interior_depth,
            )
          }
        }
      }

      // Update cache only (visual preview) — user must click Save to persist
      const dragId = dragSubunitId.current!
      const queries = queryClient.getQueriesData<Subunit[]>({ queryKey: subunitKeys.lists() })
      for (const [key] of queries) {
        queryClient.setQueryData<Subunit[]>(key, (old) =>
          old?.map((s) => (s.id === dragId ? { ...s, offsetX: newX, offsetY: newY } : s))
        )
      }
    }

    const onMouseUp = (e: mapboxgl.MapMouseEvent) => {
      const { clientX, clientY } = e.originalEvent

      if (activePlacingMode) {
        const dx = clientX - dragStartScreen.current[0]
        const dy = clientY - dragStartScreen.current[1]
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist > DRAG_THRESHOLD_PIXELS) return

        const world = getWorldPosition(clientX, clientY)
        if (world) handlePlaceSubunit(world.sceneX, world.sceneZ)
        return
      }

      if (isDragging.current) {
        isDragging.current = false
        setDragging(false)
        map.dragPan.enable()
        canvas.style.cursor = 'default'
        onSubunitUpdate?.()
      } else if (selectedSubunitId) {
        // Click outside selected subunit → deselect (but don't select new ones on click)
        const hitId = hitTestSubunits(clientX, clientY)
        if (!hitId) selectSubunit(null)
      }

      dragSubunitId.current = null
    }

    map.on('mousedown', onMouseDown)
    map.on('mousemove', onMouseMove)
    map.on('mouseup', onMouseUp)

    return () => {
      map.off('mousedown', onMouseDown)
      map.off('mousemove', onMouseMove)
      map.off('mouseup', onMouseUp)
    }
  }, [
    map, enabled, activePlacingMode, selectedSubunitId, subunits, buildings,
    hitTestSubunits, getWorldPosition, handlePlaceSubunit,
    selectSubunit, setDragging, setPreviewPosition, queryClient, onSubunitUpdate,
  ])

  // Keyboard shortcuts
  useEffect(() => {
    if (!enabled || !selectedSubunitId) return

    const subunit = subunits.find((s) => s.id === selectedSubunitId)
    if (!subunit) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete') {
        e.preventDefault()
        remove.mutate(subunit.id)
        selectSubunit(null)
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault()
        duplicate.mutate(subunit)
        return
      }

      if (e.key === 'Escape') {
        if (isPlacingMode) cancelPlacing()
        else if (isCreatingMode) cancelCreating()
        else selectSubunit(null)
        return
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    enabled, selectedSubunitId, subunits, isPlacingMode, isCreatingMode,
    remove, duplicate, selectSubunit, cancelPlacing, cancelCreating,
  ])

  // Escape to cancel placing/creating mode
  useEffect(() => {
    if (!enabled || !activePlacingMode) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isPlacingMode) cancelPlacing()
        if (isCreatingMode) cancelCreating()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled, activePlacingMode, isPlacingMode, isCreatingMode, cancelPlacing, cancelCreating])
}
